import { useQuery } from 'react-query';
import useAbortController from '../useAbortController';
import ApplicationService from '../../services/applicationService';
import useSelectedProperty from '../useSelectedProperty';

/**
 * Fetches primary applicant's portal claim status and household section completion.
 * Used to enable/disable "Send Portal Invite" for non-primary adults in AffordaPortal flows.
 *
 * @param {string} applicationId - The application ID
 * @param {Object} options - React Query options (e.g. enabled override)
 * @returns {Object} React Query result with data: { primaryIsClaimed, primaryHouseholdComplete }
 */
export default function usePrimaryPortalStatusQuery(
  applicationId: ?string,
  options: Object = {},
) {
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? null;
  const propertyId = selectedProperty?.id ?? null;
  const abortControllerOptions = useAbortController();

  const queryKey = [
    'primaryPortalStatus',
    organizationId,
    propertyId,
    applicationId,
  ];
  const enabled =
    !!organizationId &&
    !!propertyId &&
    !!applicationId &&
    options.enabled !== false;

  return useQuery(
    queryKey,
    async () => {
      const service = new ApplicationService();
      return service.getPrimaryPortalStatus(
        organizationId,
        propertyId,
        applicationId,
        abortControllerOptions,
      );
    },
    {
      ...options,
      enabled,
      refetchOnWindowFocus: false,
      onError: (error) => {
        if (options.onError) {
          options.onError(error);
        }
      },
    },
  );
}
